import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Shield,
  Users,
  Building,
  Save,
  Box,
  Globe,
  Edit,
  ArrowLeft,
} from "lucide-react";
import { toast } from "../../stores/toastStore";

interface TenantUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface BrandingConfig {
  primaryColor: string;
  secondaryColor?: string;
  companyName?: string;
}

interface Domain {
  id: string;
  name: string;
  branding: BrandingConfig;
}

interface Module {
  id?: string;
  name?: string;
  category?: string;
  description?: string;
  domain?: Domain | null;
  domainId?: string | null;
}

interface TenantSettings {
  name: string;
  branding: BrandingConfig;
  domains?: Domain[];
}

export function AdminCenter() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "users" | "settings" | "modules" | "domains"
  >("users");
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [editingModule, setEditingModule] = useState<Module | null>(null);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    } else if (activeTab === "modules") {
      fetchModules();
    } else {
      fetchSettings();
    }
  }, [activeTab]);

  const getAuthHeaders = () => {
    const storedUser = localStorage.getItem("auth_user");
    if (storedUser) {
      return { "X-Mock-User": storedUser };
    }
    return {};
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tenant/users`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data.users);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/tenant`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch settings");
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/modules`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch modules");
      const data = await res.json();
      setModules(data.modules);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load modules");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/tenant`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: settings.name,
          branding: settings.branding,
        }),
      });

      if (!res.ok) throw new Error("Failed to update settings");
      toast.success("Settings updated successfully");
      // Force reload to apply new branding/name globally if needed, or rely on context updates
      window.location.reload();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const handleSaveDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDomain) return;

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/tenant/domains/${editingDomain.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            name: editingDomain.name,
            branding: editingDomain.branding,
          }),
        },
      );

      if (!res.ok) throw new Error("Failed to update domain");
      toast.success("Domain updated successfully");
      setEditingDomain(null);
      fetchSettings();
    } catch (error) {
      toast.error("Failed to update domain");
    }
  };

  // Group modules by their core identity (name, description, category)
  const groupedModules = React.useMemo(() => {
    const groups: Record<
      string,
      {
        name: string;
        description?: string;
        category?: string;
        icon?: string;
        instances: Module[];
        accessibleDomains: string[];
      }
    > = {};

    modules.forEach((module) => {
      // Create a unique key based on core properties
      const key = `${module.name}-${module.description}-${module.category}`;

      if (!groups[key]) {
        groups[key] = {
          name: module.name || "",
          description: module.description,
          category: module.category,
          icon: module.icon,
          instances: [],
          accessibleDomains: [],
        };
      }

      groups[key].instances.push(module);

      // Track which domains have access
      if (module.domainId) {
        groups[key].accessibleDomains.push(module.domainId);
      } else {
        // If no domainId, it's tenant-wide accessible
        groups[key].accessibleDomains.push("tenant-wide");
      }
    });

    return Object.values(groups);
  }, [modules]);

  const handleDomainAccessChange = async (
    moduleInstances: Module[],
    domainId: string,
    hasAccess: boolean,
  ) => {
    try {
      if (hasAccess) {
        // Grant access - create a new module instance for this domain
        // Find a template instance to use as base
        const templateInstance =
          moduleInstances.find((m) => m.name) || moduleInstances[0];
        if (templateInstance) {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/api/modules`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                name: templateInstance.name,
                description: templateInstance.description,
                category: templateInstance.category,
                icon: templateInstance.icon,
                domainId: domainId === "tenant-wide" ? null : domainId,
              }),
            },
          );

          if (!res.ok) throw new Error("Failed to grant domain access");
          toast.success("Domain access granted");
        }
      } else {
        // Revoke access - delete the module instance for this domain
        const targetInstance = moduleInstances.find((m) =>
          domainId === "tenant-wide" ? !m.domainId : m.domainId === domainId,
        );
        if (targetInstance) {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/api/modules/${targetInstance.id}`,
            {
              method: "DELETE",
              headers: getAuthHeaders(),
            },
          );

          if (!res.ok) throw new Error("Failed to revoke domain access");
          toast.success("Domain access revoked");
        }
      }

      fetchModules(); // Refresh the list
    } catch (error) {
      toast.error("Failed to update domain access");
    }
  };

  const handleSaveModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingModule) return;

    try {
      const method = editingModule.id ? "PATCH" : "POST";
      const url = editingModule.id
        ? `${import.meta.env.VITE_API_URL}/api/modules/${editingModule.id}`
        : `${import.meta.env.VITE_API_URL}/api/modules`;

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: editingModule.name,
          description: editingModule.description,
          category: editingModule.category,
          domainId: editingModule.domain?.id || null,
        }),
      });

      if (!res.ok) throw new Error("Failed to save module");
      toast.success(
        editingModule.id
          ? "Module updated successfully"
          : "Module created successfully",
      );
      setEditingModule(null);
      fetchModules();
    } catch (error) {
      toast.error("Failed to save module");
    }
  };

  if (user?.role !== "TENANT_ADMIN") {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8 bg-destructive/10 rounded-lg text-destructive">
          <Shield size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p>You do not have permission to view the Admin Center.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-background">
      {/* Sidebar */}
      <div className="w-64 border-r border-border bg-card">
        <div className="p-6 border-b border-border">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Shield className="text-primary" />
            Admin Center
          </h1>
        </div>
        <nav className="p-4 space-y-2">
          <button
            onClick={() => setActiveTab("users")}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "users"
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users size={18} />
            Users
          </button>
          <button
            onClick={() => setActiveTab("modules")}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "modules"
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            <Box size={18} />
            Modules
          </button>
          <button
            onClick={() => setActiveTab("domains")}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "domains"
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            <Globe size={18} />
            Domains
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "settings"
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            <Building size={18} />
            Tenant Settings
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-[var(--grid-surface)]">
        <div className="max-w-4xl mx-auto p-8">
          {activeTab === "users" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold">Users</h2>
                <div className="text-sm text-muted-foreground">
                  Total: {users.length}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                        Role
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loading ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-6 py-8 text-center text-muted-foreground"
                        >
                          Loading users...
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-6 py-8 text-center text-muted-foreground"
                        >
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.map((u) => (
                        <tr
                          key={u.id}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-6 py-4 font-medium">{u.name}</td>
                          <td className="px-6 py-4 text-muted-foreground">
                            {u.email}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                u.role === "TENANT_ADMIN"
                                  ? "bg-primary/15 text-primary"
                                  : "bg-secondary text-secondary-foreground"
                              }`}
                            >
                              {u.role}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "modules" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold">Tenant Modules</h2>
                <div className="text-sm text-muted-foreground">
                  Total: {groupedModules.length}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {loading ? (
                  <p className="text-muted-foreground">Loading modules...</p>
                ) : groupedModules.length === 0 ? (
                  <p className="text-muted-foreground">No modules found.</p>
                ) : (
                  groupedModules.map((group, index) => (
                    <div
                      key={index}
                      className="p-6 bg-card border border-border rounded-lg shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-xl">
                            {group.name}
                          </h3>
                          <div className="text-xs text-muted-foreground mt-1 px-2 py-0.5 bg-muted rounded inline-block">
                            {group.category}
                          </div>
                          {group.description && (
                            <p className="text-sm text-muted-foreground mt-3">
                              {group.description}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => setEditingModule(group.instances[0])}
                            className="p-2 hover:bg-muted rounded-full transition-colors"
                            title="Edit module"
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <div className="text-sm font-medium text-muted-foreground mb-3">
                          Domain Access Control
                        </div>
                        <div className="space-y-3">
                          {/* Tenant-wide access */}
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`tenant-wide-${index}`}
                              checked={group.accessibleDomains.includes(
                                "tenant-wide",
                              )}
                              onChange={(e) =>
                                handleDomainAccessChange(
                                  group.instances,
                                  "tenant-wide",
                                  e.target.checked,
                                )
                              }
                              className="rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <label
                              htmlFor={`tenant-wide-${index}`}
                              className="text-sm font-medium"
                            >
                              All Domains (Tenant-wide)
                            </label>
                          </div>

                          {/* Individual domain access */}
                          {settings?.domains?.map((domain) => (
                            <div
                              key={domain.id}
                              className="flex items-center space-x-2"
                            >
                              <input
                                type="checkbox"
                                id={`${domain.id}-${index}`}
                                checked={group.accessibleDomains.includes(
                                  domain.id,
                                )}
                                onChange={(e) =>
                                  handleDomainAccessChange(
                                    group.instances,
                                    domain.id,
                                    e.target.checked,
                                  )
                                }
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <label
                                htmlFor={`${domain.id}-${index}`}
                                className="text-sm"
                              >
                                {domain.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Module Button */}
              <div className="flex justify-center">
                <button
                  onClick={() => setEditingModule({} as Module)}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 gap-2"
                >
                  <Box size={16} />
                  Add Module
                </button>
              </div>
            </div>
          )}

          {activeTab === "domains" && (
            <div className="space-y-6">
              {editingDomain ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setEditingDomain(null)}
                      className="p-2 hover:bg-muted rounded-full transition-colors"
                    >
                      <ArrowLeft size={20} />
                    </button>
                    <h2 className="text-2xl font-semibold">
                      Edit Domain: {editingDomain.name}
                    </h2>
                  </div>

                  <form onSubmit={handleSaveDomain} className="space-y-6">
                    <div className="bg-card border border-border rounded-lg p-6 shadow-sm space-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Domain Name
                        </label>
                        <input
                          type="text"
                          value={editingDomain.name}
                          onChange={(e) =>
                            setEditingDomain({
                              ...editingDomain,
                              name: e.target.value,
                            })
                          }
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          Company Name (Branding)
                        </label>
                        <input
                          type="text"
                          value={editingDomain.branding?.companyName || ""}
                          onChange={(e) =>
                            setEditingDomain({
                              ...editingDomain,
                              branding: {
                                ...editingDomain.branding,
                                companyName: e.target.value,
                              },
                            })
                          }
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Primary Brand Color
                          </label>
                          <div className="flex gap-3">
                            <input
                              type="color"
                              value={
                                editingDomain.branding?.primaryColor ||
                                "#000000"
                              }
                              onChange={(e) =>
                                setEditingDomain({
                                  ...editingDomain,
                                  branding: {
                                    ...editingDomain.branding,
                                    primaryColor: e.target.value,
                                  },
                                })
                              }
                              className="h-10 w-20 rounded border border-border cursor-pointer bg-background p-1"
                            />
                            <input
                              type="text"
                              value={
                                editingDomain.branding?.primaryColor ||
                                "#000000"
                              }
                              onChange={(e) =>
                                setEditingDomain({
                                  ...editingDomain,
                                  branding: {
                                    ...editingDomain.branding,
                                    primaryColor: e.target.value,
                                  },
                                })
                              }
                              className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 gap-2"
                      >
                        <Save size={16} />
                        Save Domain
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-semibold">Tenant Domains</h2>
                    <div className="text-sm text-muted-foreground">
                      Total: {settings?.domains?.length || 0}
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                            Domain Name
                          </th>
                          <th className="px-6 py-3 text-left font-medium text-muted-foreground">
                            ID
                          </th>
                          <th className="px-6 py-3 text-right font-medium text-muted-foreground">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {loading ? (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-6 py-8 text-center text-muted-foreground"
                            >
                              Loading domains...
                            </td>
                          </tr>
                        ) : !settings?.domains ||
                          settings.domains.length === 0 ? (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-6 py-8 text-center text-muted-foreground"
                            >
                              No domains found
                            </td>
                          </tr>
                        ) : (
                          settings.domains.map((d) => (
                            <tr
                              key={d.id}
                              className="hover:bg-muted/30 transition-colors"
                            >
                              <td className="px-6 py-4 font-medium">
                                {d.name}
                              </td>
                              <td className="px-6 py-4 text-muted-foreground font-mono text-xs">
                                {d.id}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button
                                  onClick={() => setEditingDomain(d)}
                                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-8 w-8"
                                >
                                  <Edit size={16} />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Module Edit Modal */}
          {editingModule && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full mx-4">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <h3 className="text-lg font-semibold">
                    {editingModule.id ? "Edit Module" : "Add Module"}
                  </h3>
                  <button
                    onClick={() => setEditingModule(null)}
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                  >
                    <ArrowLeft size={20} />
                  </button>
                </div>

                <form onSubmit={handleSaveModule} className="p-6 space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Module Name</label>
                    <input
                      type="text"
                      value={editingModule.name || ""}
                      onChange={(e) =>
                        setEditingModule({
                          ...editingModule,
                          name: e.target.value,
                        })
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description</label>
                    <textarea
                      value={editingModule.description || ""}
                      onChange={(e) =>
                        setEditingModule({
                          ...editingModule,
                          description: e.target.value,
                        })
                      }
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Category</label>
                    <select
                      value={editingModule.category || "CUSTOM"}
                      onChange={(e) =>
                        setEditingModule({
                          ...editingModule,
                          category: e.target.value as any,
                        })
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="OPERATIONS">Operations</option>
                      <option value="FINANCE">Finance</option>
                      <option value="PEOPLE">People</option>
                      <option value="SALES">Sales</option>
                      <option value="INVENTORY">Inventory</option>
                      <option value="CUSTOM">Custom</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Domain Access</label>
                    <select
                      value={editingModule.domain?.id || ""}
                      onChange={(e) => {
                        const domainId = e.target.value;
                        const domain =
                          settings?.domains?.find((d) => d.id === domainId) ||
                          null;
                        setEditingModule({
                          ...editingModule,
                          domain,
                        });
                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Tenant-wide</option>
                      {settings?.domains?.map((domain) => (
                        <option key={domain.id} value={domain.id}>
                          {domain.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setEditingModule(null)}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 gap-2"
                    >
                      <Save size={16} />
                      {editingModule.id ? "Update" : "Create"} Module
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === "settings" && settings && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold">Tenant Settings</h2>
              </div>

              <form onSubmit={handleSaveSettings} className="space-y-6">
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Company Name</label>
                    <input
                      type="text"
                      value={settings.name}
                      onChange={(e) =>
                        setSettings({ ...settings, name: e.target.value })
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <p className="text-xs text-muted-foreground">
                      This name appears in the header and system notifications.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Primary Brand Color
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="color"
                          value={settings.branding.primaryColor || "#000000"}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              branding: {
                                ...settings.branding,
                                primaryColor: e.target.value,
                              },
                            })
                          }
                          className="h-10 w-20 rounded border border-border cursor-pointer bg-background p-1"
                        />
                        <input
                          type="text"
                          value={settings.branding.primaryColor || "#000000"}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              branding: {
                                ...settings.branding,
                                primaryColor: e.target.value,
                              },
                            })
                          }
                          className="flex h-10 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 gap-2"
                  >
                    <Save size={16} />
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
